/**
 * Kiosk Settings API Client
 * Handles HTTP communication with the Kiosk API endpoint
 * Endpoint: GET/POST /kiosk/v1/admin/operator?operatorId={id}
 */

import { BASE_KIOSK_API } from '@/core/api-config';
import { TokenManager } from '@/core/auth/token-manager';
import { ApiErrorMiddleware } from '@/core/api-error-middleware';
import {
  DbActor,
  KioskSettings,
  KioskSettingsJson,
  NO_ASSOCIATED_PARTY_ERROR,
  UpdateKioskSettingsRequest
} from '../types/kiosk-settings-types';

export class KioskSettingsApi {
  constructor(
    private readonly baseUrl: string = BASE_KIOSK_API,
    private readonly tokenManager: TokenManager
  ) {}

  /**
   * Fetch kiosk settings for an operator
   * GET /kiosk/v1/admin/operator?operatorId={id}
   * @param operatorId - The operator ID
   * @returns Parsed kiosk settings with metadata
   */
  async getKioskSettings(operatorId: number): Promise<KioskSettings> {
    const endpoint = `/admin/operator?operatorId=${operatorId}`;

    try {
      console.log(`[KioskSettingsApi] Fetching from: ${this.baseUrl}${endpoint}`);

      const headers = await this.tokenManager.getAuthHeaders();
      const response = await ApiErrorMiddleware.fetchWithErrorHandling(
        `${this.baseUrl}${endpoint}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...headers
          }
        },
        'KioskSettings',
        endpoint
      );

      const data = (await response.json()) as DbActor;

      console.log('[KioskSettingsApi] Response received:', {
        operatorId: data.id,
        hasSettings: !!data.settings,
        settingsLength: data.settings?.length
      });

      // Parse stringified settings JSON
      let parsedSettings: KioskSettingsJson = {};
      if (data.settings) {
        try {
          parsedSettings = JSON.parse(data.settings);
        } catch (e) {
          console.error('[KioskSettingsApi] Failed to parse settings JSON:', e);
          parsedSettings = {};
        }
      }

      let countryCode = data.countryCode;
      let partyId = data.partyId;

      // Sub-operators never have their own countryCode/partyId in the DB (backend
      // enforces this) — resolve the effective one from the parent for display only.
      // This must never be sent back in an update request for this actor.
      if (data.parentId != null && !partyId) {
        const parent = await this.getKioskSettings(data.parentId);
        countryCode = parent.countryCode;
        partyId = parent.partyId;
      }

      return {
        operatorId: data.id,
        parentId: data.parentId,
        countryCode,
        partyId,
        settings: parsedSettings
      };
    } catch (error) {
      console.error('[KioskSettingsApi] Failed to fetch settings:', error);
      throw error;
    }
  }

  /**
   * Update kiosk settings
   * POST /kiosk/v1/admin/addOperator
   * IMPORTANT: The API REPLACES the entire settings object, not merges.
   * We must send the full current settings, not just the delta.
   * @param operatorId - The operator ID
   * @param countryCode - Country code. For a sub-operator this is the value already
   *                       resolved from its parent by getKioskSettings (never a literal fallback).
   * @param partyId - Party ID. For a sub-operator this is the value already resolved
   *                  from its parent by getKioskSettings.
   * @param parentId - Parent actor ID; present when this actor is a sub-operator. Not sent
   *                   to the API — addOperator ignores it, so it's kept here for logging only.
   * @param delta - Full current settings (not just changed fields)
   */
  async updateKioskSettings(
    operatorId: number,
    countryCode: string | undefined,
    partyId: string | undefined,
    parentId: number | null | undefined,
    delta: UpdateKioskSettingsRequest
  ): Promise<void> {
    const endpoint = `/admin/addOperator`;

    try {
      console.log('[KioskSettingsApi] Updating settings:', {
        operatorId,
        countryCode,
        partyId,
        parentId,
        settingsFields: Object.keys(delta)
      });

      if (!partyId) {
        throw new Error(NO_ASSOCIATED_PARTY_ERROR);
      }

      const headers = await this.tokenManager.getAuthHeaders();

      // CRITICAL: Send full current settings, not delta
      // The API replaces the entire settings object, so we must include all fields
      const dbActorRequest: DbActor = {
        id: operatorId,
        countryCode: countryCode || 'US',
        partyId,
        settings: JSON.stringify(
          delta.connected === 'OCPP' ? { ...delta, connected: undefined } : delta
        ) // This should be the FULL settings object
      };

      const response = await ApiErrorMiddleware.fetchWithErrorHandling(
        `${this.baseUrl}${endpoint}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...headers,
            'X-Operator-Id': operatorId.toString()
          },
          body: JSON.stringify(dbActorRequest)
        },
        'KioskSettings',
        endpoint,
        [400, 401, 403, 404, 405, 422, 500]
      );

      console.log('[KioskSettingsApi] Settings updated successfully');
    } catch (error) {
      console.error('[KioskSettingsApi] Failed to update settings:', error);
      throw error;
    }
  }
}
